import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { IndexedDBService } from './indexeddb.service';

export interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  phase: 'initial' | 'editing';
  showSkip?: boolean;
  showNext?: boolean;
  showPrevious?: boolean;
  action?: () => void;
  iframeTarget?: string; // CSS selector for elements inside iframe
  iframeSelector?: string; // Selector for the iframe itself
}

export interface TourState {
  isActive: boolean;
  currentStep: number;
  currentPhase: 'initial' | 'editing' | 'completed';
  hasCompletedInitial: boolean;
  hasCompletedEditing: boolean;
  hasSeenTour: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GuidedTourService {
  private tourStateSubject = new BehaviorSubject<TourState>({
    isActive: false,
    currentStep: 0,
    currentPhase: 'initial',
    hasCompletedInitial: false,
    hasCompletedEditing: false,
    hasSeenTour: false
  });
  
  public tourState$: Observable<TourState> = this.tourStateSubject.asObservable();

  private readonly TOUR_STORAGE_KEY = 'guidedTour';
  private isBrowser: boolean;

  // Phase 1: Initial Tour Steps
  private initialTourSteps: TourStep[] = [
    {
      id: 'welcome',
      title: '🎉 Welcome to Website Builder!',
      content: 'Let me show you how to create your professional website in just a few simple steps.',
      target: '.chat-header',
      position: 'bottom',
      phase: 'initial',
      showNext: true,
      showSkip: true
    },
    {
      id: 'chat-experience',
      title: '💬 Chat with AI Assistant',
      content: 'Use this chat to tell me about your business. I\'ll ask you questions and help create content for your website. You can see all changes happen in real-time!',
      target: '.chat-messages',
      position: 'left',
      phase: 'initial',
      showNext: true,
      showPrevious: true
    },
    {
      id: 'live-preview',
      title: '👁️ Live Website Preview',
      content: 'Watch your website come to life here! Every change you make will be reflected instantly in this preview.',
      target: '.preview-iframe',
      position: 'left',
      phase: 'initial',
      showNext: true,
      showPrevious: true
    },
    {
      id: 'theme-picker',
      title: '🎨 Customize Your Theme',
      content: 'Choose your website\'s color theme anytime! Click this button to see different color schemes and pick the one that matches your brand.',
      target: '.theme-picker-btn',
      position: 'bottom',
      phase: 'initial',
      showNext: true,
      showPrevious: true
    },
    {
      id: 'save-progress',
      title: '💾 Save Your Progress',
      content: 'Don\'t worry about losing your work! Click this button anytime to save your progress. Your data is safely stored and you can continue later.',
      target: '.save-progress-btn',
      position: 'bottom',
      phase: 'initial',
      showNext: true,
      showPrevious: true
    },
    {
      id: 'get-started',
      title: '🚀 Ready to Start?',
      content: 'Great! Now you\'re ready to begin. Start by telling me about your business in the chat, and I\'ll help you create an amazing website!',
      target: '.chat-input-container',
      position: 'top',
      phase: 'initial',
      showPrevious: true,
      action: () => this.completeInitialTour()
    }
  ];

  // Phase 2: Editing Tour Steps
  private editingTourSteps: TourStep[] = [
    {
      id: 'edit-mode-toggle',
      title: '✏️ Edit Mode Available!',
      content: 'Great! Now you can edit your website content. Click this toggle to switch between viewing and editing modes.',
      target: '.edit-mode-btn',
      position: 'bottom',
      phase: 'editing',
      showNext: true,
      showSkip: true
    },
    {
      id: 'click-to-edit',
      title: '👆 How to Edit Content',
      content: 'To edit text content: First enable edit mode using the toggle button above, then click on any text in the website preview. You\'ll see a blue glow around editable text when you hover over it. Click on the text to open the content editor.',
      target: '.edit-mode-btn',
      position: 'bottom',
      phase: 'editing',
      showNext: true,
      showPrevious: true
    },
    {
      id: 'image-upload',
      title: '🖼️ How to Upload Images',
      content: 'To replace images: First enable edit mode using the toggle button above, then click on any image in the website preview. You\'ll see a blue glow around images when you hover over them. Click on an image to open the image uploader and replace it with your own.',
      target: '.edit-mode-btn',
      position: 'bottom',
      phase: 'editing',
      showNext: true,
      showPrevious: true
    },
    {
      id: 'deploy-button',
      title: '🌐 Deploy Your Website',
      content: 'Once you\'re happy with your website, click this button to publish it online and make it live for everyone to see!',
      target: '.deploy-btn',
      position: 'bottom',
      phase: 'editing',
      showPrevious: true,
      action: () => this.completeEditingTour()
    }
  ];

  constructor(
    private indexedDBService: IndexedDBService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Initialize the tour service and check if user has seen the tour
   */
  async initializeTour(companyGuid: string): Promise<void> {
    if (!this.isBrowser) return;

    console.log('🎯 Initializing tour for company GUID:', companyGuid);

    try {
      // Load tour state from IndexedDB
      const savedTourState = await this.indexedDBService.getMetadata(`${this.TOUR_STORAGE_KEY}_${companyGuid}`);
      
      if (savedTourState) {
        this.tourStateSubject.next(savedTourState);
        console.log('🎯 Tour state loaded from storage:', savedTourState);
      } else {
        // First time user - show initial tour
        console.log('🎯 First time user detected - preparing initial tour');
        this.tourStateSubject.next({
          isActive: false,
          currentStep: 0,
          currentPhase: 'initial',
          hasCompletedInitial: false,
          hasCompletedEditing: false,
          hasSeenTour: false
        });
      }
    } catch (error) {
      console.error('Failed to initialize tour:', error);
    }
  }

  /**
   * Start the initial tour (Phase 1)
   */
  async startInitialTour(): Promise<void> {
    const currentState = this.tourStateSubject.value;
    
    console.log('🎯 startInitialTour called with state:', currentState);
    
    // CRITICAL FIX: Prevent starting tour if already completed OR already active
    if (currentState.hasSeenTour && currentState.hasCompletedInitial) {
      console.log('🎯 User has already completed initial tour');
      return;
    }
    
    if (currentState.isActive && currentState.currentPhase === 'initial') {
      console.log('🎯 Initial tour is already active, skipping duplicate start');
      return;
    }

    const newState = {
      ...currentState,
      isActive: true,
      currentStep: 0,
      currentPhase: 'initial' as const,
      hasSeenTour: true
    };

    this.tourStateSubject.next(newState);

    console.log('🎯 Initial tour started immediately - new state:', newState);
  }

  /**
   * Start the editing tour (Phase 2)
   */
  async startEditingTour(): Promise<void> {
    const currentState = this.tourStateSubject.value;
    
    if (currentState.hasCompletedEditing) {
      console.log('🎯 User has already completed editing tour');
      return;
    }

    this.tourStateSubject.next({
      ...currentState,
      isActive: true,
      currentStep: 0,
      currentPhase: 'editing'
    });

    console.log('🎯 Starting editing tour');
  }

  /**
   * Get current tour step
   */
  getCurrentStep(): TourStep | null {
    const state = this.tourStateSubject.value;
    if (!state.isActive) return null;

    const steps = state.currentPhase === 'initial' ? this.initialTourSteps : this.editingTourSteps;
    return steps[state.currentStep] || null;
  }

  /**
   * Go to next step
   */
  nextStep(): void {
    const state = this.tourStateSubject.value;
    const steps = state.currentPhase === 'initial' ? this.initialTourSteps : this.editingTourSteps;
    
    if (state.currentStep < steps.length - 1) {
      this.tourStateSubject.next({
        ...state,
        currentStep: state.currentStep + 1
      });
    } else {
      // Execute action if it exists
      const currentStep = steps[state.currentStep];
      if (currentStep.action) {
        currentStep.action();
      }
    }
  }

  /**
   * Go to previous step
   */
  previousStep(): void {
    const state = this.tourStateSubject.value;
    
    if (state.currentStep > 0) {
      this.tourStateSubject.next({
        ...state,
        currentStep: state.currentStep - 1
      });
    }
  }

  /**
   * Skip the current tour phase
   */
  skipTour(): void {
    const state = this.tourStateSubject.value;
    
    if (state.currentPhase === 'initial') {
      this.completeInitialTour();
    } else if (state.currentPhase === 'editing') {
      this.completeEditingTour();
    }
  }

  /**
   * Complete the initial tour
   */
  private async completeInitialTour(): Promise<void> {
    const state = this.tourStateSubject.value;
    const newState = {
      ...state,
      isActive: false,
      hasCompletedInitial: true,
      currentPhase: 'completed' as const
    };

    this.tourStateSubject.next(newState);
    await this.saveTourState(newState);
    console.log('🎯 Initial tour completed');
  }

  /**
   * Complete the editing tour
   */
  private async completeEditingTour(): Promise<void> {
    const state = this.tourStateSubject.value;
    const newState = {
      ...state,
      isActive: false,
      hasCompletedEditing: true,
      currentPhase: 'completed' as const
    };

    this.tourStateSubject.next(newState);
    await this.saveTourState(newState);
    console.log('🎯 Editing tour completed');
  }

  /**
   * Check if user should see initial tour
   */
  shouldShowInitialTour(): boolean {
    const state = this.tourStateSubject.value;
    return !state.hasSeenTour || (!state.hasCompletedInitial && !state.isActive);
  }

  /**
   * Check if user should see editing tour
   */
  shouldShowEditingTour(): boolean {
    const state = this.tourStateSubject.value;
    return state.hasCompletedInitial && !state.hasCompletedEditing && !state.isActive;
  }

  /**
   * Save tour state to IndexedDB
   */
  private async saveTourState(state: TourState): Promise<void> {
    if (!this.isBrowser) return;

    try {
      // We'll need to get the companyGuid from somewhere - let's add it as a parameter
      // For now, we'll store it with a generic key and update this later
      await this.indexedDBService.storeMetadata(`${this.TOUR_STORAGE_KEY}_current`, state);
      console.log('🎯 Tour state saved to storage');
    } catch (error) {
      console.error('Failed to save tour state:', error);
    }
  }

  /**
   * Save tour state with company GUID
   */
  async saveTourStateForUser(companyGuid: string): Promise<void> {
    if (!this.isBrowser) return;

    try {
      const state = this.tourStateSubject.value;
      await this.indexedDBService.storeMetadata(`${this.TOUR_STORAGE_KEY}_${companyGuid}`, state);
      console.log('🎯 Tour state saved for user:', companyGuid);
    } catch (error) {
      console.error('Failed to save tour state for user:', error);
    }
  }

  /**
   * Reset tour state (for testing or re-showing)
   */
  async resetTour(companyGuid: string): Promise<void> {
    const resetState: TourState = {
      isActive: false,
      currentStep: 0,
      currentPhase: 'initial',
      hasCompletedInitial: false,
      hasCompletedEditing: false,
      hasSeenTour: false
    };

    this.tourStateSubject.next(resetState);
    
    if (this.isBrowser) {
      await this.indexedDBService.storeMetadata(`${this.TOUR_STORAGE_KEY}_${companyGuid}`, resetState);
    }
    
    console.log('🎯 Tour state reset for user:', companyGuid);
  }

  /**
   * Debug method to manually start tour (for testing)
   */
  debugStartTour(phase: 'initial' | 'editing' = 'initial'): void {
    console.log('🎯 Debug: Starting tour phase:', phase);
    
    if (phase === 'initial') {
      this.startInitialTour();
    } else {
      this.startEditingTour();
    }
  }

  /**
   * Get current tour state for debugging
   */
  getTourState(): TourState {
    return this.tourStateSubject.value;
  }

  /**
   * Reposition the current tour step (useful for enhancing positioning after components load)
   */
  repositionCurrentStep(): void {
    const currentState = this.tourStateSubject.value;
    if (currentState.isActive) {
      console.log('🎯 Repositioning current tour step...');
      // Trigger a reposition by updating the state
      this.tourStateSubject.next({ ...currentState });
    }
  }
} 